import React, { useState } from 'react'
import useOtpDigits, { OTP_LENGTH } from '../../../utils/useOtpDigits'
import useResendCooldown from '../../../utils/useResendCooldown'
import EmailVerificationEnvelopeIllustration from './EmailVerificationEnvelopeIllustration'
import EmailVerificationButtonLabel from './EmailVerificationButtonLabel'

const RESEND_COOLDOWN_SEC = 60

// resendAvailableAt is when the backend's per-record cooldown (off lastSentAt)
// actually clears; seed/refresh the local countdown from it so "Resend" isn't
// clickable immediately after a send just because this is a fresh mount.
function cooldownSecondsFromTimestamp(resendAvailableAt) {
  if (!resendAvailableAt) return 0
  const seconds = Math.ceil((new Date(resendAvailableAt).getTime() - Date.now()) / 1000)
  return seconds > 0 ? seconds : 0
}

// Generic 6-digit code entry screen, shared by the verify-primary,
// update-primary and add-backup steps of the email verification flow.
const EmailVerificationCodeEntry = (props) => {
  const {
    email, title, description, resendAvailableAt, verifiedMessage, showIllustration,
    onSubmitCode, onResend, onBack, helpNote,
    onConfirmedAction, confirmedActionLabel,
  } = props
  // once the code is accepted, the boxes stay read-only so editing them can't
  // re-send verify for the already-used verification
  const {
    code, isComplete, reset: resetDigits, getInputProps,
  } = useOtpDigits({ onComplete: handleSubmitDigits, locked: !!verifiedMessage })
  const [submitLoading, setSubmitLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [resendLoading, setResendLoading] = useState(false)
  const {
    resendCooldown, sentMessageVisible, startCooldown, clearCooldown,
  } = useResendCooldown(cooldownSecondsFromTimestamp(resendAvailableAt))

  async function handleSubmitDigits(submittedCode) {
    if (verifiedMessage || submittedCode.length < OTP_LENGTH) return
    try {
      setSubmitLoading(true)
      setErrorMessage('')
      await onSubmitCode(submittedCode)
    } catch (err) {
      setSubmitLoading(false)
      setErrorMessage(err.message || 'Incorrect code. Please try again.')
      resetDigits()
    }
  }

  // a wrong code clears the digits, so this also covers the error state
  const submitDisabled = submitLoading || !isComplete

  function handleSubmit(e) {
    e.preventDefault()
    handleSubmitDigits(code)
  }

  function renderResendControl() {
    if (sentMessageVisible) {
      return (
        <span className='emailVerificationFlow-codeEntry-resend-success'>
          Sent!
        </span>
      )
    }
    if (resendCooldown > 0) {
      return (
        <button type='button' className='emailVerificationFlow-codeEntry-resend-btn' disabled>
          {`Resend available in ${resendCooldown}s`}
        </button>
      )
    }
    return (
      <button
        type='button'
        className='emailVerificationFlow-codeEntry-resend-btn'
        onClick={handleResend}
        disabled={resendLoading}
      >
        {resendLoading ? 'Sending...' : 'Resend'}
      </button>
    )
  }

  async function handleResend() {
    try {
      setResendLoading(true)
      clearCooldown()
      setErrorMessage('')
      const response = await onResend()
      startCooldown(cooldownSecondsFromTimestamp(response && response.resendAvailableAt) || RESEND_COOLDOWN_SEC)
    } catch (err) {
      setErrorMessage(err.message || 'Failed to resend code. Please try again.')
    } finally {
      setResendLoading(false)
    }
  }

  return (
    <div className='emailVerificationFlow-codeEntry'>
      {onBack && (
        <button type='button' className='emailVerificationFlow-backBtn' onClick={onBack}>
          ← Back
        </button>
      )}
      {showIllustration && <EmailVerificationEnvelopeIllustration />}
      <div className='emailVerificationFlow-title'>{title}</div>
      <div className='emailVerificationFlow-emailDisplay'>{email}</div>
      <div className='emailVerificationFlow-description'>{description}</div>
      <form className='emailVerificationFlow-codeEntry-main' onSubmit={handleSubmit}>
        <div className='emailVerificationFlow-codeEntry-digitRow'>
          {Array.from({ length: OTP_LENGTH }, (_, index) => (
            // eslint-disable-next-line react/no-array-index-key, react/jsx-props-no-spreading
            <input key={index} className='emailVerificationFlow-codeEntry-digitInput' {...getInputProps(index)} />
          ))}
        </div>
        {errorMessage && (
          <div className='emailVerificationFlow-error'>{errorMessage}</div>
        )}
        {!verifiedMessage && (
          <div className='emailVerificationFlow-codeEntry-resend'>
            {renderResendControl()}
          </div>
        )}
        {verifiedMessage && (
          <div className='emailVerificationFlow-codeEntry-verifiedMessage'>{verifiedMessage}</div>
        )}
        {!verifiedMessage && helpNote && (
          <div className='emailVerificationFlow-codeEntry-helpNote'>{helpNote}</div>
        )}
        {verifiedMessage ? (
          <button
            type='button'
            className='btn btn--blue btn--large'
            onClick={onConfirmedAction}
          >
            {confirmedActionLabel}
          </button>
        ) : (
          <button
            type='submit'
            className={`btn btn--blue btn--large${submitDisabled ? ' btn--disabled' : ''}`}
            disabled={submitDisabled}
          >
            <EmailVerificationButtonLabel label='Continue' loading={submitLoading} />
          </button>
        )}
      </form>
    </div>
  )
}

EmailVerificationCodeEntry.defaultProps = {
  showIllustration: true,
}

export default EmailVerificationCodeEntry
